import React, { FC, MouseEvent } from "react";
import { Box, Typography } from "@mui/material";
import styled from "@emotion/styled";
import { Album } from "@models/album";
import PhotoPlaceholder from "@components/PhotoPlaceholder";
import { useTranslation } from "react-i18next";
import { DateUtil } from "@utils/date.util";
import { useNavigate } from "react-router-dom";
import TextIconButton from "@components/TextIconButton";
import { Mode } from "@store/homeSlice";
import { MmsOutlined } from "@mui/icons-material";
import PhotoAlbumOutlinedIcon from "@mui/icons-material/PhotoAlbumOutlined";
import { ActivityType } from "@models/activity-type";
import { DashboardClickableTile } from "@components/Home/DashboardClickableTile";

const Container = styled.div`
  border-radius: 4px;
  border-width: 1px;
  border-color: rgb(189, 193, 202, 0.4);
  border-style: solid;
  padding: 10px 20px 16px;
  height: 100%;
  box-sizing: border-box;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const StyledCheckbox = styled.input`
  width: 16px;
  height: 16px;
  cursor: pointer;
`;

const Image = styled.img`
  width: auto;
  height: 166px;
  object-fit: cover;
  border-radius: 4px;
`;

const AlbumContent: FC<{
  album: Album;
  onCheck?: () => void;
  checked?: boolean;
  showDashboardIcon?: boolean;
}> = ({ album, onCheck, checked, showDashboardIcon = false }) => {
  const { t } = useTranslation();

  return (
    <Box display="flex" flexDirection="column" gap="16px">
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        gap="16px"
      >
        <Typography
          color="textSecondary"
          fontSize="20px"
          fontWeight="500"
          lineHeight="30px"
          marginBottom="6px"
          noWrap={true}
        >
          {album.allPhotos ? t("albums.allPhotosAlbumLabel") : album.name}
        </Typography>
        {onCheck && !album.allPhotos ? (
          <StyledCheckbox
            type="checkbox"
            checked={checked}
            onChange={onCheck}
            onClick={(e) => e.stopPropagation()}
          />
        ) : showDashboardIcon ? (
          <Box sx={{ color: "primary.main", display: "flex" }}>
            <PhotoAlbumOutlinedIcon fontSize="small" />
          </Box>
        ) : null}
      </Box>
      {album.coverPhoto ? (
        <Image src={album.coverPhoto.fileUrl} alt={album.name} />
      ) : (
        <PhotoPlaceholder width="100%" height="auto" />
      )}

      <Box
        display="flex"
        justifyContent="space-between"
        fontSize="14px"
        lineHeight="22px"
        fontWeight="400"
        color="#171A1FFF"
      >
        <span>
          {t("dashboard.albums.numberOfPhotos", {
            photos: album.photoCount || 0,
          })}
        </span>
        <span>{DateUtil.formatTime(album.lastUpdatedDate)}</span>
      </Box>
    </Box>
  );
};

const AlbumItem: FC<{
  album: Album;
  onCheck?: () => void;
  checked?: boolean;
  mode?: Mode;
  to?: string;
}> = ({ album, onCheck, checked, mode = Mode.Caregiver, to }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const navigateToCompanion = (event: MouseEvent) => {
    event.stopPropagation();
    navigate(`/portal/talk`, {
      state: {
        albumId: album.id,
        activityType: ActivityType.GallerySession,
        isPatient: true,
      },
    });
  };

  if (to) {
    return (
      <DashboardClickableTile to={to} tileVariant="album">
        <AlbumContent album={album} showDashboardIcon />
      </DashboardClickableTile>
    );
  }

  return (
    <Container>
      <AlbumContent album={album} onCheck={onCheck} checked={checked} />

      {mode === Mode.Patient && (
        <Box display="flex" justifyContent="center" marginTop="24px">
          <TextIconButton
            onClick={navigateToCompanion}
            variant="contained"
            color="success"
            size="large"
            iconLeft={true}
            text={t("patientDashboard.albums.watchButton")}
            icon={MmsOutlined}
          />
        </Box>
      )}
    </Container>
  );
};

export default AlbumItem;
